import React, { useEffect } from "react";
import { Col, Row, Button, Modal, Timeline } from "antd";
import {
  IAppState,
  ServiceUsersContext,
} from "../../../context/ServiceUsersContext";
import ButtonComponent from "../ButtonComponent/ButtonComponent";

import { IServiceUser } from "../Interfaces/IServiceUser";
import { ITripDetails } from "../Interfaces/ITripDetails";
import DashBoardGmaps from "../../DashBoard/DashBoardGmaps";
import { getServiceUserTripDetails } from "../ServiceUserModel/ServiceUserModel";
import { getFormattedDate, getFormattedTime } from "../Utils";
import { defaultTripDetails } from "./defaultTripDetails";
import { Link, useHistory } from "react-router-dom";
import commonStyles1 from "../../DashBoard/DashBoardMobile/DashBoardMobile.module.scss";
import commonStyles2 from "../../DashBoard/DashBoard.module.scss";
import {
  addFavoritesMobileIcon,
  backArrowIcon,
  cancelIcon,
  dropoffTimelineIcon,
  homeIcon,
  pickupTimelineIcon,
  rebookIcon,
} from "../../../assets/images";
import moment from "moment";
import getTripIcon from "../../../helpers/getTripIcon";

interface Props {
  parentPersonId: number;
  personId: number;
  typeOfService: string;
  serviceUserName: string;
  serviceDetailsList: Array<ITripDetails>;
}

const ServiceUsersDetailsMobile: React.FC<Props> = ({
  parentPersonId,
  personId,
  typeOfService,
  serviceUserName,
  serviceDetailsList,
}) => {
  const [title, setTitle] = React.useState<string>("");
  const [serviceDetails, setServiceDetails] = React.useState<ITripDetails>(
    defaultTripDetails
  );
  const [isModalVisible, setIsModalVisible] = React.useState<boolean>(false);
  const [isMapModalOpen, setIsMapModalOpen] = React.useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    if (typeOfService === "upcoming") {
      const item = serviceDetailsList.filter(function(obj) {
        return obj.personId === Number(personId);
      })[0];
      if (item !== undefined) {
        setServiceDetails(item);
        setTitle("Upcoming Services");
      }
    }
  }, [serviceDetailsList]);

  const handleBack = () => {
    history.goBack();
  };

  const handleClose = (): void => {
    setIsModalVisible(false);
  };

  const handleCancelService = (): void => {
    setIsModalVisible(true);
  };

  const serviceDetailRow = (
    leftHeading: string,
    leftContent: string,
    rightHeading: string,
    rightContent: string
  ): JSX.Element => {
    return (
      <Row className={commonStyles1.serviceDetailsRows}>
        <Col span={12}>
          <Row>
            <Col span={24} className={commonStyles1.detailsHeading}>
              {leftHeading}
            </Col>
            <Col span={24} className={commonStyles1.detailsInfo}>
              {leftContent}
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={24} className={commonStyles1.detailsHeading}>
              {rightHeading}
            </Col>
            <Col span={24} className={commonStyles1.detailsInfo}>
              {rightContent}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderButtons = () => {
    if (!serviceDetails?.statusDetail) return null;

    const createFavoritesBtn = (span: number) => (
      <Col span={span} className={commonStyles1.btnAlign}>
        <Button type="default" className={commonStyles1.favoritesBtn}>
          <div className={commonStyles1.favoritesBtnContent}>
            <img src={addFavoritesMobileIcon} />
            Add to Favorites
          </div>
        </Button>
      </Col>
    );

    const rebookBtn = (
      <Col
        span={12}
        className={`${commonStyles1.btnAlign} ${commonStyles1.noRipple}`}
      >
        <Button type="default" className={commonStyles1.rebookBtn}>
          <div className={commonStyles1.rebookBtnContent}>
            <img src={rebookIcon} alt="rebook service button" />
            Rebook Service
          </div>
        </Button>
      </Col>
    );

    const cancelBtn = (
      <Col
        span={12}
        className={`${commonStyles1.btnAlign} ${commonStyles1.noRipple}`}
      >
        <Button
          type="default"
          className={commonStyles1.cancelBtn}
          onClick={handleCancelService}
        >
          <div className={commonStyles1.cancelBtnContent}>
            <img src={cancelIcon} alt="rebook service button" />
            Cancel Service
          </div>
        </Button>
      </Col>
    );

    if (serviceDetails.statusDetail === "Completed") {
      return (
        <Row
          className={`${commonStyles1.btnsRow} ${commonStyles1.noContainerMargin}`}
          justify="center"
        >
          {createFavoritesBtn(12)}
          {rebookBtn}
        </Row>
      );
    } else {
      return (
        <Row className={commonStyles1.btnsRow} gutter={[15, 15]}>
          {createFavoritesBtn(24)}
          {rebookBtn}
          {cancelBtn}
        </Row>
      );
    }
  };

  return (
    <>
      <div className={commonStyles1.serviceContainer}>
        <Row>
          <Col span={24}>
            <Button
              type="link"
              className={commonStyles1.backBtn}
              onClick={handleBack}
            >
              <img src={backArrowIcon} alt="back button" />
              Back
            </Button>
          </Col>
        </Row>
        <Row className={commonStyles1.serviceTitleMargin}>
          <Col span={24}>
            <p className={commonStyles1.title}>Service Details</p>
          </Col>
        </Row>

        {serviceDetailRow(
          "Date",
          moment(serviceDetails?.tripDate).format("MM/DD/YY"),
          "Time",
          moment(serviceDetails?.appointmentTime).format("HH:mm A")
        )}

        <Row className={commonStyles1.serviceDetailsRows}>
          <Col span={12}>
            <Row>
              <Col span={24} className={commonStyles1.detailsHeading}>
                Service Type
              </Col>
              <Col span={24} style={{ textAlign: "center" }}>
                <span className={commonStyles1.tripImageSmall}>
                  {getTripIcon(serviceDetails?.tripReason)}
                </span>
                <span className={commonStyles1.detailsInfo}>
                  {serviceDetails?.tripReason}
                </span>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row>
              <Col span={24} className={commonStyles1.detailsHeading}>
                Service User
              </Col>
              <Col span={24} className={commonStyles1.detailsInfo}>
                {serviceDetails?.serviceUser}
              </Col>
            </Row>
          </Col>
        </Row>

        {serviceDetailRow(
          "Cost",
          `$${serviceDetails?.tripCost}`,
          "Status",
          serviceDetails?.statusDetail
        )}

        <div className={commonStyles1.mobileMapsContainer}>
          <Row align="middle" justify="center">
            <Col
              xs={{ span: 4, offset: 2 }}
              sm={{ span: 4, offset: 4 }}
              md={{ span: 4, offset: 6 }}
              className={commonStyles2.distanceText}
            >
              <Row justify="end" align="middle" style={{ marginRight: "5px" }}>
                <Col>{serviceDetails?.providerDistance} mi</Col>
              </Row>
            </Col>
            <Col
              xs={18}
              sm={16}
              md={14}
              className={commonStyles2.customTimeline}
            >
              <Timeline>
                <Timeline.Item dot={<img src={pickupTimelineIcon} />}>
                  <Row>
                    <Col span={24} className={commonStyles2.addressHeading}>
                      Pickup Address
                    </Col>
                    <Col span={24} className={commonStyles2.addressDetails1}>
                      <img src={homeIcon} alt="pickup address" />
                      <span>{serviceDetails?.pickUpAddress1}</span>
                    </Col>
                    <Col span={24} className={commonStyles2.addressDetails2}>
                      {serviceDetails?.pickUpCity},{" "}
                      {serviceDetails?.pickUpState}{" "}
                      {serviceDetails?.pickUpZipCode}
                    </Col>
                  </Row>
                </Timeline.Item>
                <Timeline.Item dot={<img src={dropoffTimelineIcon} />}>
                  <Row>
                    <Col span={24} className={commonStyles2.addressHeading}>
                      Dropoff Address
                    </Col>
                    <Col span={24} className={commonStyles2.addressDetails1}>
                      {serviceDetails?.dropOffAddress1}
                    </Col>
                    <Col span={24} className={commonStyles2.addressDetails2}>
                      {serviceDetails?.dropOffCity},{" "}
                      {serviceDetails?.dropOffState}{" "}
                      {serviceDetails?.dropOffZipCode}
                    </Col>
                  </Row>
                </Timeline.Item>
              </Timeline>
            </Col>
          </Row>

          <DashBoardGmaps
            setIsMapModalOpen={setIsMapModalOpen}
            tripDetails={serviceDetails}
            width={"auto"}
            height="167px"
            isCalling="mobile"
          />
        </div>

        {serviceDetailRow(
          "Pickup Instructions",
          serviceDetails?.pickupNotes,
          "Dropoff Instructions",
          serviceDetails?.dropNotes
        )}

        {serviceDetailRow(
          "Trip ID",
          serviceDetails?.tripId,
          "Booking ID",
          serviceDetails?.bookingId
        )}

        {renderButtons()}
      </div>

      <Modal
        visible={isMapModalOpen}
        footer={null}
        className={commonStyles1.expandedMap}
      >
        <DashBoardGmaps
          isMapModalOpen={isMapModalOpen}
          setIsMapModalOpen={setIsMapModalOpen}
          tripDetails={serviceDetails}
          width={"auto"}
          height={"458px"}
          isCalling="mobile"
        />
      </Modal>

      <Modal
        visible={isModalVisible}
        centered={true}
        className={commonStyles1.mobileModalContainer}
        footer={
          <Row
            justify="center"
            gutter={[0, 10]}
            className={commonStyles1.mobileModalBtns}
          >
            <Col className={commonStyles1.noRipple}>
              <Button
                className={commonStyles1.mobileModalCloseBtn}
                onClick={handleClose}
              >
                Close
              </Button>
            </Col>
            <Col className={commonStyles1.noRipple}>
              <Button className={commonStyles1.mobileModalYesCancelBtn}>
                Yes, Cancel
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center">
          <Col className={commonStyles1.mobileModalHeading}>Cancel Service</Col>
        </Row>
        <Row justify="center">
          <Col className={commonStyles1.mobileModalContent}>
            Are you sure you want to cancel
            <br />
            this service? This cannot be
            <br /> undone.
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ServiceUsersDetailsMobile;
